import { useState, useEffect } from "react";
import GYAxios from "./GYAxios";
import moment from "moment-timezone";

export function useSolarCalc() {
	/**
	 * Format and parse number based on decimal place specified
	 * @param {number} num
	 * @param {decimal place} dec
	 * @returns formatted number
	 */
	const parseFormatNumber = (num, dec) => {
		const divisor = getDivisor(dec);
		const value = Math.round(num * divisor) / divisor;

		return value;
	};

	/**
	 * Get Divisor
	 * @param {number} dec
	 * @returns Divisor
	 */
	const getDivisor = (dec) => {
		let num = "1";

		for (let i = 0; i < dec; i++) {
			num += "0";
		}
		return parseInt(num);
	};

	/**
	 * Calculate unit cost
	 * @param {Unit Price} price
	 * @param {Discount} discount
	 * @returns
	 */
	const calculateUnitCost = (price, discount, currency, specialDP) => {
		let dec = 0;
		if (specialDP != null) {
			dec = specialDP;
		} else {
			switch (currency) {
				case "THB":
					dec = 6;
					break;
				case "VND":
					dec = 2;
					break;

				default:
					break;
			}
		}
		const value = price * (1 - discount);
		const roundOff = parseFormatNumber(value, dec);

		return roundOff;
	};

	/**
	 * Calculate savings
	 * @param {Solar Production} prod
	 * @param {Unit price} price
	 * @param {Discount} discount
	 * @returns savings object
	 */
	const calculateSavings = (prod, price, discount, currency) => {
		let res = "";
		let dec = 0;

		switch (currency) {
			case "THB":
				dec = 2;
				break;

			default:
				break;
		}

		try {
			const calcON = prod.ON * (price.ON * discount.ON);
			const savingsON = parseFormatNumber(calcON, dec);

			const calcOFF = prod.OFF * (price.OFF * discount.OFF);
			const savingsOFF = parseFormatNumber(calcOFF, dec);

			const calcFT = prod.TOTAL * (price.FT * discount.FT);
			const savingsFT = parseFormatNumber(calcFT, dec);

			const calcTotal =
				currency === "THB"
					? savingsON + savingsOFF + savingsFT
					: savingsON + savingsOFF;
			const savingsTotal = parseFormatNumber(calcTotal, dec);

			res = {
				savingsON,
				savingsOFF,
				savingsFT,
				savingsTotal,
			};
		} catch (error) {
			console.log("error @ calculateSavings:", error);
			res = "-";
		}

		return res;
	};

	/**
	 * Calculate solar production costs
	 * @param {Solar Production} prod
	 * @param {Unit Price} price
	 * @param {Discount} discount
	 * @returns
	 */
	const calculateProdCost = (prod, price, discount, currency, specialDP) => {
		let res = {};
		let dec = 0;

		switch (currency) {
			case "THB":
				dec = 2;
				break;

			default:
				break;
		}

		try {
			const calcON =
				prod.ON * calculateUnitCost(price.ON, discount.ON, currency, specialDP);
			const costON = parseFormatNumber(calcON, dec);

			const calcOFF =
				prod.OFF *
				calculateUnitCost(price.OFF, discount.OFF, currency, specialDP);
			const costOFF = parseFormatNumber(calcOFF, dec);

			const calcFT =
				prod.TOTAL *
				calculateUnitCost(price.FT, discount.FT, currency, specialDP);
			const costFT = parseFormatNumber(calcFT, dec);

			const costTotal = parseFormatNumber(costON + costOFF + costFT, dec);

			res = {
				prod_on: prod.ON,
				prod_off: prod.OFF,
				costON,
				costOFF,
				costFT,
				costTotal,
				unitcostON: calculateUnitCost(
					price.ON,
					discount.ON,
					currency,
					specialDP,
				),
				unitcostOFF: calculateUnitCost(
					price.OFF,
					discount.OFF,
					currency,
					specialDP,
				),
			};
		} catch (error) {
			console.log("error @ calculateProdCost:", error);
		}

		return res;
	};

	/**
	 * Calculate production based on contract type
	 * @param {obj} prod
	 * @param {obj} EVNInputs
	 * @returns solar production object
	 */
	const calculateProd = (prod, EVNInputs) => {
		const solarON = parseFormatNumber(prod.solarON, 3);
		const solarOFF = parseFormatNumber(prod.solarOFF, 3);
		const solarTotal = parseFormatNumber(prod.solarTotal, 3);

		const EVNOn =
			EVNInputs != null ? parseFormatNumber(EVNInputs.EVN_on, 3) : 0;
		const EVNOff =
			EVNInputs != null ? parseFormatNumber(EVNInputs.EVN_off, 3) : 0;
		const EVNTotal =
			EVNInputs != null ? parseFormatNumber(EVNInputs.EVN_total, 3) : 0;

		switch (prod.contractID) {
			case "FIT":
				return {
					solarON: EVNOn,
					solarOFF: EVNOff,
					solarTotal: EVNTotal,
				};

			case "SCFITCL":
			case "SCFITGY":
				const tempON =
					solarON > EVNOn ? parseFormatNumber(solarON - EVNOn, 3) : 0;
				const tempOFF =
					solarOFF > EVNOff ? parseFormatNumber(solarOFF - EVNOff, 3) : 0;
				const tempTotal = parseFormatNumber(tempON + tempOFF, 3);

				return {
					solarON: tempON,
					solarOFF: tempOFF,
					solarTotal: tempTotal,
				};

			default:
				return {
					solarON,
					solarOFF,
					solarTotal,
				};
		}
	};

	/**
	 * Calculate VAT and total due
	 * @param {Subtotal due} total
	 * @returns VAT object
	 */
	const calculateVAT = (total, currency, vatPercent) => {
		let dec = 0;

		switch (currency) {
			case "THB":
				dec = 2;
				break;
			case "VND":
				dec = 0;
				break;

			default:
				break;
		}

		const VAT = parseFormatNumber(total * vatPercent, dec);
		const totalDue = parseFormatNumber(total, dec) + VAT;

		return {
			VAT,
			totalDue: parseFormatNumber(totalDue, dec),
		};
	};

	/**
	 * Calculate Irradiation
	 * @param {Irradiation per minute} irr
	 * @returns Irradiation per 10 minutes (kWh/m2)
	 */
	const calculateIrr = (irr) => irr / 6000;

	/**
	 * Calculate PR
	 * @param {Irradiation per minute} irr
	 * @returns Irradiation per 10 minutes (kWh/m2)
	 */
	const calculatePR = (obj) =>
		parseFloat(
			((obj.prod / ((obj.irr / 6000) * obj.capacity)) * 100).toFixed(2),
		);

	/**
	 * Formula to calculate Performance Indice
	 * @param {Data object} obj
	 */
	const calculatePIndice = (obj) =>
		parseFloat(
			(
				(obj.actualProd / obj.expProd - obj.actualIrr / obj.expIrr) *
				100
			).toFixed(2),
		);

	/**
	 * Calculate values for shared revenue section
	 * @param {string} contractID
	 * @param {number} sharedPercent
	 * @param {obj} EVNInputs
	 * @returns Values for shared revenue section
	 */
	const calculateSharedRevenue = (
		contractID,
		sharedPercent,
		EVNTotal,
		FITPrice,
	) => {
		const evnTotal = parseFormatNumber(EVNTotal, 3);
		const unitCostValue = parseFormatNumber(FITPrice * sharedPercent, 2);
		const unitCost = contractID === "SCFITGY" ? -unitCostValue : unitCostValue;
		const subtotal = parseFormatNumber(evnTotal * unitCost);

		return {
			sharedPercent,
			evnTotal,
			unitCost,
			subtotal,
		};
	};

	/**
	 * Calculate unused
	 * @param {number} netPR
	 * @param {number} brutPR
	 * @param {number} capacity
	 * @param {number} irradiance
	 * @returns unused production of the month
	 */
	const calculateUnused = (netPR, brutPR, capacity, irradiance) =>
		((netPR - brutPR) * capacity * irradiance) / 100;

	/**
	 * Calculate DEO
	 * @param {number} theoreticalPR
	 * @param {number} capacity
	 * @param {number} irradiance
	 * @returns deemed energy output
	 *
	 * Logic:
	 * 1.	Check if GA and TPA loss is a non-negative number; set them to 0 if negative.
	 * 2.	Calculate DEO = [ Real PR  x  Capacity (kWp)  x  Total Irradiance (kWh/m2) ] – GA loss – TPA loss
	 * 3.	Check if DEO >  Total Solar Production (kWh); else set DEO = Total Solar Production (kWh)
	 * 4.	Consumption % = Total Solar Production (kWh)  /  Deemed Energy Output (kWh)
	 */
	const DEOCalc = (
		theoreticalPR,
		capacity,
		irr,
		GA_irr,
		GA_prod,
		TPA_irr,
		TPA_prod,
	) => {
		const _DEO = (theoreticalPR / 100) * capacity * irr;
		const _GA_loss =
			(((theoreticalPR / 100) * GA_irr) / 6000) * capacity - GA_prod;
		const _TPA_loss =
			(((theoreticalPR / 100) * TPA_irr) / 6000) * capacity - TPA_prod;

		const GA_loss = _GA_loss < 0 ? 0 : _GA_loss;
		const TPA_loss = _TPA_loss < 0 ? 0 : _TPA_loss;

		const DEO = _DEO - GA_loss - TPA_loss;

		return DEO;
	};

	/**
	 * Calculate monthly consumption
	 * @param {number} prod
	 * @param {number} deo
	 * @returns monthly consumption
	 */
	const consumptionCalc = (prod, deo) => (prod / deo) * 100;

	/**
	 * Calculate FT after discount
	 * @param {number} ft_discount
	 * @param {number} FT_cost
	 * @returns FT after discount
	 */
	const calcFTA = (discount, cost) => {
		if (cost === null) {
			return null;
		} else {
			switch (true) {
				case discount === null:
					return cost;
				case discount === 0:
					return 0;
				case discount > 0:
					return cost - cost * discount;
				default:
					return;
			}
		}
	};

	// calculate MSA before August 2024
	const MSAB4Aug24 = (price, quantity, discount) => {
		if (discount) {
			return price * quantity + discount;
		}
		return price * quantity;
	};
	const calcMSASubtotal = (price, quantity, discount) => {
		if (quantity === 0) {
			return price * quantity + discount;
		}
	};
	/**
	 * calculate Incidence Loss
	 * RPR x Capacity x Monthly Incidence Irradiation - Monthly Incidence Production -> Check negative, if <0 then = 0
	 * @param {number} RPR
	 * @param {number} power
	 * @param {number} irr - irradiance, not irradiation
	 * @param {number} prod
	 */
	const cal_inc_loss = (RPR, power, irr, prod) => {
		const _RPR = parseFloat(RPR);
		const _power = parseFloat(power);
		const _irr = parseFloat(irr);
		const _prod = parseFloat(prod);

		const _loss =
			parseFloat(_RPR) * parseFloat(_power) * (parseFloat(_irr) / 6000) -
			parseFloat(_prod);
		return _loss < 0 ? 0 : _loss;
	};

	/**
	 * calculate DEO based on Hoang's formula
	 * DEO kWh = RPR x Capacity x Monthly Total Irradiation - Monthly TPA Loss - Monthly GA Loss
	 * @param {number} RPR
	 * @param {number} power
	 * @param {number} irr_sum, not irradiation
	 * @param {number} tpa_loss
	 * @param {number} ga_loss
	 */
	const cal_new_DEO = (RPR, power, irr_sum, tpa_loss, ga_loss) => {
		const _DEO =
			parseFloat(RPR) * parseFloat(power) * (parseFloat(irr_sum) / 6000) -
			parseFloat(tpa_loss) -
			parseFloat(ga_loss);
		return _DEO;
	};

	/**
	 * calculate used energy based on DEO and actual production
	 * @param {number} DEO
	 * @param {number} prod
	 */
	const cal_unused_scheme = (DEO, prod) => {
		const _unused = parseFloat(DEO) - parseFloat(prod);
		return _unused < 0 ? 0 : _unused;
	};

	return {
		calculateSavings,
		calculateProdCost,
		calculateProd,
		calculateVAT,
		calculateIrr,
		calculatePR,
		calculatePIndice,
		calculateUnitCost,
		calculateSharedRevenue,
		calculateUnused,
		DEOCalc,
		parseFormatNumber,
		consumptionCalc,
		calcFTA,
		calcMSASubtotal,
		MSAB4Aug24,
		cal_inc_loss,
		cal_new_DEO,
		cal_unused_scheme,
	};
}

export const useSaving = ({
	table_names,
	country,
	date,
	month,
	is_sum_by_site,
}) => {
	const [savingData, setSavingData] = useState([]);
	const table_names_str = Array.isArray(table_names)
		? table_names.join(",")
		: table_names?.toString();
	const month_str =
		month && Array.isArray(month)
			? month.map((m) => m.format("YYYYMM")).join(",")
			: month?.format("YYYYMM");
	const date_str = date?.format("YYYY-MM-DD");
	useEffect(() => {
		const fetchData = async () => {
			const paramObj = {
				month: month_str,
			};
			if (date_str) {
				paramObj.date = date_str;
			}
			if (table_names_str) {
				paramObj.table_names = table_names_str;
			}
			if (country) {
				paramObj.country = country;
			}
			if (is_sum_by_site) {
				paramObj.is_sum_by_site = is_sum_by_site;
			}
			const params = new URLSearchParams(paramObj).toString();
			const res = await GYAxios.get(`/invoice/savings?${params}`);
			setSavingData(res.data);
		};
		if (month_str) {
			fetchData();
		}
	}, [table_names_str, country, month_str, date_str, is_sum_by_site]);
	return savingData;
};
